import { useEffect, useRef } from "react";
import { Noise } from "noisejs";

function colorsEqual(a, b) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i].length !== b[i].length) return false;
    for (let j = 0; j < a[i].length; j++) {
      if (a[i][j] !== b[i][j]) return false;
    }
  }
  return true;
}

export default function AnimatedBackground({ className, color }) {
  const canvasRef = useRef();
  const animationFrameRef = useRef();
  const noise = useRef(new Noise(Math.random())).current;
  const prevColorRef = useRef();

  // Linear interpolation between 2 RGB colors
  const lerpColor = (a, b, t) =>
    a.map((v, i) => Math.round(v + (b[i] - v) * t));

  // Get interpolated color from palette based on t in [0,1]
  const getPaletteColor = (t) => {
    const n = color.length - 1;
    const scaledT = Math.min(Math.max(t, 0), 1) * n;
    const i = Math.floor(scaledT);
    const frac = scaledT - i;
    if (i >= n) return color[n];
    return lerpColor(color[i], color[i + 1], frac);
  };

  useEffect(() => {
    if (prevColorRef.current && colorsEqual(prevColorRef.current, color)) {
      return;
    }
  
    cancelAnimationFrame(animationFrameRef.current);
    prevColorRef.current = color;
  
    let isCancelled = false; // 👈 protection supplémentaire
  
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const targetFPS = 10;
    const frameDelay = 1000 / targetFPS;
    let lastDrawTime = 0;
  
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
  
    resize();
    window.addEventListener("resize", resize);
  
    let time = 0;
    const scale = 0.003;
    const precision = 6;
  
    const draw = () => {
      if (isCancelled) return;
      const now = performance.now();
  
      if (now - lastDrawTime < frameDelay) {
        animationFrameRef.current = requestAnimationFrame(draw);
        return;
      }
      lastDrawTime = now;
  
      const { width, height } = canvas;
      const imageData = ctx.createImageData(width, height);
      const data = imageData.data;
  
      for (let y = 0; y < height; y += precision) {
        for (let x = 0; x < width; x += precision) {
          const n = noise.perlin3(x * scale, y * scale, time);
          const t = (n + 1) / 2;
          const [r, g, b] = getPaletteColor(t);
  
          for (let dy = 0; dy < precision; dy++) {
            for (let dx = 0; dx < precision; dx++) {
              const px = (y + dy) * width + (x + dx);
              const i = px * 4;
              data[i] = r;
              data[i + 1] = g;
              data[i + 2] = b;
              data[i + 3] = 255;
            }
          }
        }
      }
  
      ctx.putImageData(imageData, 0, 0);
      time += 0.03;
      animationFrameRef.current = requestAnimationFrame(draw);
  };

  draw();

  return () => {
    isCancelled = true;
    cancelAnimationFrame(animationFrameRef.current);
    window.removeEventListener("resize", resize);
  };
}, [color]);

  return (
    <canvas
      ref={canvasRef}
      style={
        className
          ? {}
          : {
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              zIndex: 1,
              pointerEvents: "none",
            }
      }
      className={className}
    />
  );
}
